import axios from "axios";
import { Info, Trash2, TriangleAlert } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { base_url } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";

interface StudentProofProps {
    ready: (f:string) => void;
}

const StudentProof: React.FC<StudentProofProps> = ({ ready }) => {
    const [fileError, setFileError] = React.useState("");
    const { setError, data } = useContext(RegistrationContext);
    const [fileUploaded, setFileUploaded] = useState(data.student_proof_file_name);

    useEffect(() => {
        ready(fileUploaded);
    }, [fileUploaded, ready]);
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];


            // Check file size (max 5MB)
            if (selectedFile.size > 5 * 1024 * 1024) {
                setFileError("File size exceeds 5MB limit");

                return;
            }

            // Check file type
            const validTypes = ["application/pdf", "image/jpeg", "image/png"];
            if (!validTypes.includes(selectedFile.type)) {
                setFileError("Only PDF files are accepted");
                return;
            }


            try {
                const formData = new FormData();

                // Append the file if the registration type requires it
                if (selectedFile) {
                    formData.append("file", selectedFile);
                }

                await axios.post(`${base_url}/api/update_file`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                setFileUploaded(selectedFile.name);

            } catch (error: any) {
                setError("Unexpected error. " + error.response.data.message);
            }
        }
    };
    const handleFileRemove = async () => {

        setFileError("");
        try {
            await axios.post(`${base_url}/api/remove_file`, {});
            setFileUploaded(false);
        } catch (error: any) {
            setError("Unexpected error. " + error.response.data.message);
        }
    };

    const handleViewFile = async () => {
        try {
            const response = await axios.get(`${base_url}/api/view_file`, {
                params: { file_name: data.student_proof_file_name },
                responseType: "blob", // Important to handle binary data
            });

            // Create a URL for the file and open it in a new tab
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const newTab = window.open(fileURL, "_blank");
            if (!newTab) {
                alert("Please allow popups to view the file.");
            }
        } catch (error: any) {
            setError("Unexpected error. " + error.response?.data?.message || error.message);
        }
    };

    if (fileUploaded) {
        return (
            <li className="flex flex-col border border-gray-300 p-4 rounded-lg mt-8 bg-blue-50">
                <div className="flex items-start mb-2">
                    <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">
                        Student Proof Uploaded
                    </span>
                </div>
                <div className="ml-7 space-y-2">
                    <p className="text-sm flex items-center space-x-2">
                        <button
                            onClick={() => handleViewFile()}
                            className="text-blue-500 hover:text-blue-700 underline"
                        >
                            {fileUploaded}
                        </button>
                        <button
                            onClick={() => handleFileRemove()}
                            className="text-red-600 hover:text-red-700"
                            aria-label="Remove file"
                        >
                            <Trash2 className="h-5 w-5" />
                        </button>
                    </p>
                </div>
            </li>
        );
    } else {
        return (
            <li className="flex flex-col border border-red-300 p-4 rounded-lg mt-8 bg-red-50">

                <div className="flex items-start mb-2">
                    <TriangleAlert className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">
                        Student Proof Required
                    </span>
                </div>


                <div className="ml-7 space-y-2">
                    <label
                        htmlFor="student-proof-2"
                        className="text-sm font-medium block"
                    >
                        Upload proof of student status (PDF, max 5MB)
                    </label>

                    <input
                        id="student-proof-2"
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-200 file:text-red-700 hover:file:bg-red-300"
                    />

                    {fileError && (
                        <p className="text-sm text-red-500">
                            {fileError}
                        </p>
                    )}
                </div>
            </li>
        );
    }

}




export default StudentProof;